/**
 * Engagement-funnel events fired from the frontend (Engaged tier).
 *
 * Backend dedups per (userId, date, kind) — calling these multiple times in
 * the same day is safe and cheap. See docs/FeatureDesigns/teacher-stats.md.
 */

import { API_BASE, getAuthHeaders } from './config';

type FrontendActivityKind = 'question_view' | 'attempt_view' | 'solution_analysis_view';

function recordActivity(kind: FrontendActivityKind): void {
  // Fire-and-forget — don't await or surface errors.
  fetch(`${API_BASE}/events/activity`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind }),
  }).catch(() => {});
}

export function recordQuestionView(): void {
  recordActivity('question_view');
}

export function recordAttemptView(): void {
  recordActivity('attempt_view');
}

/** The student is looking at a question's solution analysis. Deduped per
 *  (user, day) server-side, so firing on every panel render is safe. */
export function recordSolutionAnalysisView(): void {
  recordActivity('solution_analysis_view');
}

/**
 * Device/viewport telemetry — fire ONCE per session (sessionStorage-gated) so it's
 * one tiny beacon per session, never per-request. Backend keeps one row per
 * (userId, date) in user-daily-activity. Used to measure the screen-size
 * distribution of the user base (drives responsive-layout investment decisions).
 */
export function recordClientContext(): void {
  try {
    if (sessionStorage.getItem('client-ctx-sent') === '1') return;
    sessionStorage.setItem('client-ctx-sent', '1');
  } catch { /* sessionStorage unavailable — skip */ }

  const innerWidth = window.innerWidth;
  const innerHeight = window.innerHeight;
  if (!innerWidth || !innerHeight) return;
  const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const deviceClass: 'phone' | 'tablet' | 'desktop' = innerWidth < 768 ? 'phone' : coarse ? 'tablet' : 'desktop';
  const dpr = Math.round((window.devicePixelRatio || 1) * 100) / 100;
  const theme = effectiveTheme();

  fetch(`${API_BASE}/events/activity`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind: 'client_context', client: { innerWidth, innerHeight, dpr, deviceClass, theme } }),
  }).catch(() => {});
}

/**
 * Resolve the concrete light/dark the user is actually seeing, mirroring
 * @shared-ui/theme's ThemeProvider ('system' → OS preference). Read directly
 * from localStorage/matchMedia so this stays a plain (non-hook) beacon.
 */
function effectiveTheme(): 'light' | 'dark' {
  let stored: string | null = null;
  try { stored = localStorage.getItem('padcanvas-theme'); } catch { /* unavailable */ }
  if (stored === 'light' || stored === 'dark') return stored;
  const prefersDark = typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}
